import { AlertCircle, Check, ChevronDown, Minus, Plus, Sparkles, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useAuth } from "../../auth/AuthContext";
import { ApiError, apiRequest, describeApiError } from "../../lib/api";
import { track } from "../../lib/analytics";
import { photoBackground } from "../Cards";
import { ButtonLabel } from "../ButtonLabel";
import { Modal } from "../Modal";
import { useReservationConfig } from "../../lib/queries";
import {
  OCCUPANCY_INFO, ROOM_TYPES, amountWithCurrency, autoRooms, departureLabel, estimateTotal, occupancyLabel, roomCapacity, roomSuggestions, upcomingDepartures
} from "../../lib/reservations";
import type { ApiEnvelope, Occupancy, ReservationRequest, TripPage } from "../../types";

/**
 * Flow A — "¡Estás a un paso de tu próxima aventura!" The reservation request modal (4a): trip
 * panel on the left, form on the right; a full-height sheet on mobile. Both entry points
 * ("Reservar" and the planning modal's "Crear apartado") open this same component (FR2).
 *
 * The CTA stays disabled until every required field and confirmation is valid, validation sits
 * next to the field it concerns, and a retry reuses the same idempotency key so a double tap can
 * never create two folios (A4, AC3).
 */

export interface ReservationPrefill {
  departureDate?: string | null;
  adults?: number;
  children?: number;
  childAges?: number[];
  rooms?: Occupancy[];
  /** The season row picked on the trip page (hotel category, land only or with flights). */
  serviceCategory?: string | null;
}

const HERO_SHADE = "linear-gradient(180deg, rgba(0,37,87,0.2) 0%, rgba(0,37,87,0.92) 100%)";

export function ReservationModal({ trip, open, onOpenChange, prefill, duplicatedFrom, onSubmitted, entryPoint = "trip_page" }: {
  trip: TripPage;
  open: boolean;
  onOpenChange(open: boolean): void;
  prefill?: ReservationPrefill;
  /** Set when re-submitting from "Duplicar solicitud con otras fechas". */
  duplicatedFrom?: string;
  onSubmitted(request: ReservationRequest): void;
  /** Where the customer came from, for analytics only (FR2: both routes run this same modal). */
  entryPoint?: "trip_page" | "planning_modal" | "history_duplicate" | "mobile_bar";
}) {
  const { user } = useAuth();
  const config = useReservationConfig();
  const departures = useMemo(() => upcomingDepartures(trip.content, trip.earliestDeparture), [trip]);
  const [departure, setDeparture] = useState<string | null>(prefill?.departureDate && departures.includes(prefill.departureDate) ? prefill.departureDate : departures[0] ?? null);
  const [adults, setAdults] = useState(prefill?.adults ?? 2);
  const [hasChildren, setHasChildren] = useState((prefill?.children ?? 0) > 0);
  const [children, setChildren] = useState(prefill?.children ?? 0);
  const [ages, setAges] = useState<number[]>(prefill?.childAges ?? []);
  const [rooms, setRooms] = useState<Occupancy[]>(prefill?.rooms ?? autoRooms((prefill?.adults ?? 2) + (prefill?.children ?? 0)));
  const [passport, setPassport] = useState(false);
  const [fullName, setFullName] = useState(user?.fullName ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [phone, setPhone] = useState(user?.phone ?? "");
  const [moreDates, setMoreDates] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string[] | null>(null);
  const [existing, setExisting] = useState<{ id: string; folio: string } | null>(null);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  // One key per attempt series: a retry after a network error reuses it (idempotent), a fresh
  // successful submission mints a new one for the next request.
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [availableDepartures, setAvailableDepartures] = useState<string[] | null>(null);

  useEffect(() => { if (open) track("reservation_modal_opened", { entry_point: entryPoint, trip_id: trip.id }); }, [open, entryPoint, trip.id]);
  useEffect(() => { if (user) { setFullName((value) => value || user.fullName); setEmail((value) => value || user.email); setPhone((value) => value || user.phone || ""); } }, [user]);
  useEffect(() => { if (!hasChildren) { setChildren(0); setAges([]); } else if (children === 0) { setChildren(1); setAges([8]); } }, [hasChildren, children]);
  useEffect(() => { setAges((current) => Array.from({ length: children }, (_, index) => current[index] ?? 8)); }, [children]);

  const travelers = adults + children;
  const maxTravelers = config.data?.maxTravelers ?? 12;
  const maxChildAge = config.data?.maxChildAge ?? 17;
  const capacity = roomCapacity(rooms);
  const fitOk = capacity === travelers && rooms.length <= adults && rooms.length > 0;
  const serviceCategory = prefill?.serviceCategory ?? null;
  const estimate = useMemo(() => estimateTotal(trip.content, rooms, departure, serviceCategory), [trip, rooms, departure, serviceCategory]);
  const dateList = availableDepartures ?? departures;
  const visibleDates = moreDates ? dateList : dateList.slice(0, 6);

  const fieldErrors = {
    departure: departure ? null : "Elige una fecha de salida habilitada.",
    travelers: travelers < 1 ? "Debe viajar al menos una persona." : travelers > maxTravelers ? `Podemos atender hasta ${maxTravelers} viajeros por solicitud.` : null,
    ages: hasChildren && ages.some((age) => age > maxChildAge) ? `Un menor tiene hasta ${maxChildAge} años.` : null,
    rooms: !rooms.length ? "Agrega al menos una habitación." : capacity !== travelers ? `Las habitaciones alojan ${capacity} personas y viajan ${travelers}.` : rooms.length > adults ? "Cada habitación necesita al menos un adulto." : null,
    passport: passport ? null : "Requerido para continuar con la solicitud.",
    fullName: fullName.trim().length >= 2 ? null : "Ingresa tu nombre completo.",
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? null : "Ingresa un correo electrónico válido.",
    phone: /^\+?[0-9\s-]{10,20}$/.test(phone.trim()) ? null : "Ingresa un teléfono de 10 dígitos (puedes incluir la lada +52)."
  };
  const valid = Object.values(fieldErrors).every((value) => value === null);

  const bump = (setter: (value: number) => void, value: number, delta: number, min: number, max: number) => setter(Math.max(min, Math.min(max, value + delta)));
  const changeTravelers = (delta: number) => {
    const next = Math.max(1, Math.min(maxTravelers, travelers + delta));
    const nextAdults = Math.max(1, next - children);
    setAdults(nextAdults);
    if (next - nextAdults !== children) { setChildren(next - nextAdults); }
    setRooms(autoRooms(next));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid || !departure) return;
    setSubmitting(true); setError(null);
    try {
      const payload = await apiRequest<ApiEnvelope<{ request: ReservationRequest; duplicate: boolean }>>("/api/v1/reservations", {
        method: "POST",
        headers: { "idempotency-key": idempotencyKey },
        body: JSON.stringify({
          tripSlug: trip.slug, departureDate: departure, adults, children, childAges: ages, rooms, passportConfirmed: true,
          ...(serviceCategory ? { serviceCategory } : {}),
          contact: { fullName: fullName.trim(), email: email.trim(), phone: phone.trim() },
          ...(duplicatedFrom ? { duplicatedFrom } : {}), confirmDuplicate
        })
      });
      setIdempotencyKey(crypto.randomUUID());
      onSubmitted(payload.data.request);
    } catch (cause) {
      const code = cause instanceof ApiError ? cause.code : "NETWORK";
      track("reservation_request_failed", { error_category: code, retryable: code !== "RESERVATION_EXISTS", entry_point: entryPoint });
      if (cause instanceof ApiError && cause.code === "RESERVATION_EXISTS") {
        const details = cause.details as { requestId?: string; folio?: string } | undefined;
        setExisting(details?.requestId && details.folio ? { id: details.requestId, folio: details.folio } : null);
        setError([cause.message]);
      } else if (cause instanceof ApiError && cause.code === "DEPARTURE_UNAVAILABLE") {
        const details = cause.details as { departures?: string[] } | undefined;
        if (details?.departures) { setAvailableDepartures(details.departures); setDeparture(details.departures[0] ?? null); }
        setError([cause.message]);
      } else {
        setError(describeApiError(cause, "No pudimos enviar tu solicitud. Tu información se conserva; intenta de nuevo."));
      }
    } finally { setSubmitting(false); }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} label="Solicitar reservación">
      <div className="dialog-content resv-dialog">
        <button type="button" className="dialog-close" onClick={() => onOpenChange(false)} aria-label="Cerrar"><X size={20} aria-hidden="true" /></button>
        <aside className="resv-trip" style={photoBackground(trip.heroImageUrl, HERO_SHADE, "half")} aria-label="Resumen del viaje">
          <span className="hero-eyebrow">{[trip.countries[0], trip.durationDays ? `${trip.durationDays} días${trip.durationNights ? ` / ${trip.durationNights} noches` : ""}` : null].filter(Boolean).join(" · ")}</span>
          <h3>{trip.name}</h3>
          <dl className="resv-recap">
            <div><dt>Fecha de salida</dt><dd translate="no">{departure ? departureLabel(departure, true) : "Por elegir"}</dd></div>
            <div><dt>Viajeros</dt><dd translate="no">{adults} {adults === 1 ? "adulto" : "adultos"}{children ? ` · ${children} ${children === 1 ? "niño" : "niños"}` : ""}</dd></div>
            <div><dt>Habitaciones</dt><dd>{occupancyLabel(rooms)}</dd></div>
            {serviceCategory && <div><dt>Tarifa</dt><dd>{serviceCategory}</dd></div>}
          </dl>
          {estimate && (
            <div className="resv-estimate">
              <span>Estimado · por persona</span>
              <strong translate="no">{amountWithCurrency(estimate.perPerson, estimate.currency)}</strong>
              <small>Promedio según la distribución de habitaciones. Tarifa sujeta a confirmación del agente; no se realiza ningún cargo ahora.</small>
            </div>
          )}
        </aside>

        <form className="resv-form" onSubmit={submit} noValidate>
          <span className="page-eyebrow">Solicitud de reservación</span>
          <h2>¡Estás a un paso de tu próxima aventura!</h2>
          <p>Cuéntanos algunos detalles y un agente de viajes de Aventuria te contactará para revisar disponibilidad y realizar la reservación.</p>

          <fieldset className="resv-block">
            <legend>Fecha de salida <span aria-hidden="true">*</span></legend>
            <div className="chip-row date-chips" role="radiogroup" aria-label="Fecha de salida">
              {visibleDates.map((date) => (
                <button key={date} type="button" role="radio" aria-checked={departure === date} className={`chip${departure === date ? " active" : ""}`} onClick={() => setDeparture(date)} translate="no">
                  {departureLabel(date)}
                </button>
              ))}
              {dateList.length > 6 && (
                <button type="button" className="link-button" onClick={() => setMoreDates((value) => !value)}>{moreDates ? "Menos fechas" : `Más fechas (${dateList.length - 6})`} <ChevronDown size={14} aria-hidden="true" /></button>
              )}
              {!dateList.length && <span className="field-hint">Este viaje no tiene salidas habilitadas por ahora.</span>}
            </div>
            <span className="resv-notice"><AlertCircle size={14} aria-hidden="true" /> La fecha habilitada está sujeta a disponibilidad y no garantiza la reservación.</span>
            <span className="field-hint">Salidas con al menos {config.data?.minLeadDays ?? 28} días de anticipación.</span>
            {fieldErrors.departure && <span className="field-error" role="alert">{fieldErrors.departure}</span>}
          </fieldset>

          <div className="resv-block">
            <span className="field-label" id="resv-pax">¿Cuántas personas viajan? <span aria-hidden="true">*</span></span>
            <div className="stepper" role="group" aria-labelledby="resv-pax">
              <button type="button" onClick={() => changeTravelers(-1)} disabled={travelers <= 1} aria-label="Menos viajeros"><Minus size={16} aria-hidden="true" /></button>
              <strong translate="no">{travelers} {travelers === 1 ? "viajero" : "viajeros"}</strong>
              <button type="button" onClick={() => changeTravelers(1)} disabled={travelers >= maxTravelers} aria-label="Más viajeros"><Plus size={16} aria-hidden="true" /></button>
            </div>
            {fieldErrors.travelers && <span className="field-error" role="alert">{fieldErrors.travelers}</span>}
          </div>

          <div className="resv-block">
            <span className="field-label" id="resv-kids">¿Viajan niños? <span aria-hidden="true">*</span></span>
            <div className="chip-row" role="radiogroup" aria-labelledby="resv-kids">
              <button type="button" role="radio" aria-checked={!hasChildren} className={`chip${!hasChildren ? " active" : ""}`} onClick={() => setHasChildren(false)}>No</button>
              <button type="button" role="radio" aria-checked={hasChildren} className={`chip${hasChildren ? " active" : ""}`} onClick={() => setHasChildren(true)}>Sí</button>
            </div>
            {hasChildren && (
              <div className="resv-kids">
                <div className="stepper small" role="group" aria-label="Número de niños">
                  <button type="button" onClick={() => { bump(setChildren, children, -1, 1, Math.max(1, travelers - 1)); }} disabled={children <= 1} aria-label="Menos niños"><Minus size={14} aria-hidden="true" /></button>
                  <strong translate="no">{children} {children === 1 ? "niño" : "niños"}</strong>
                  <button type="button" onClick={() => { if (adults > 1) { setAdults(adults - 1); setChildren(children + 1); } }} disabled={adults <= 1} aria-label="Más niños"><Plus size={14} aria-hidden="true" /></button>
                </div>
                <span className="field-label">Edad de los niños <span aria-hidden="true">*</span></span>
                <div className="age-grid">
                  {ages.map((age, index) => (
                    <label key={index} className="age-field">
                      <span>Niño {index + 1}</span>
                      <div className="stepper small">
                        <button type="button" onClick={() => setAges(ages.map((value, i) => (i === index ? Math.max(0, value - 1) : value)))} aria-label={`Menos edad niño ${index + 1}`}><Minus size={14} aria-hidden="true" /></button>
                        <strong translate="no">{age} {age === 1 ? "año" : "años"}</strong>
                        <button type="button" onClick={() => setAges(ages.map((value, i) => (i === index ? Math.min(maxChildAge, value + 1) : value)))} aria-label={`Más edad niño ${index + 1}`}><Plus size={14} aria-hidden="true" /></button>
                      </div>
                    </label>
                  ))}
                </div>
                <span className="field-hint">Menores de 12 años comparten habitación con dos adultos y pueden aplicar a tarifa reducida.</span>
                {fieldErrors.ages && <span className="field-error" role="alert">{fieldErrors.ages}</span>}
              </div>
            )}
          </div>

          <div className="resv-block">
            <div className="label-row">
              <span className="field-label" id="resv-rooms">Ocupación <span aria-hidden="true">*</span></span>
              <button type="button" className="link-button" onClick={() => setRooms(autoRooms(travelers))}><Sparkles size={13} aria-hidden="true" /> Sugerir distribución</button>
            </div>
            <span className="field-hint">Distribuye a tus {travelers} viajeros en habitaciones. Puedes combinar tipos, por ejemplo dos dobles y una triple.</span>
            <div className="chip-row">
              {roomSuggestions(travelers).map((suggestion) => {
                const active = occupancyLabel(rooms) === suggestion.label;
                return <button key={suggestion.label} type="button" className={`chip${active ? " active" : ""}`} aria-pressed={active} onClick={() => setRooms(suggestion.rooms)}>{suggestion.label}</button>;
              })}
            </div>
            <ul className="room-list" aria-labelledby="resv-rooms">
              {rooms.map((room, index) => (
                <li key={index} className="room-row">
                  <span className="room-name">Habitación {index + 1}</span>
                  <div className="room-types" role="radiogroup" aria-label={`Tipo de habitación ${index + 1}`}>
                    {ROOM_TYPES.map((type) => (
                      <button key={type} type="button" role="radio" aria-checked={room === type} className={`room-type${room === type ? " active" : ""}`} onClick={() => setRooms(rooms.map((value, i) => (i === index ? type : value)))}>
                        <strong>{OCCUPANCY_INFO[type].short}</strong><small>{OCCUPANCY_INFO[type].long} · {OCCUPANCY_INFO[type].capacity}</small>
                      </button>
                    ))}
                  </div>
                  <button type="button" className="icon-button ghost" onClick={() => setRooms(rooms.filter((_, i) => i !== index))} aria-label={`Quitar habitación ${index + 1}`} disabled={rooms.length <= 1}><Trash2 size={15} aria-hidden="true" /></button>
                </li>
              ))}
            </ul>
            <button type="button" className="link-button" onClick={() => setRooms([...rooms, "double"])} disabled={rooms.length >= adults}><Plus size={13} aria-hidden="true" /> Agregar habitación</button>
            <span className={`fit-message${fitOk ? " ok" : ""}`} role="status">
              {fitOk ? <Check size={14} aria-hidden="true" /> : <AlertCircle size={14} aria-hidden="true" />}
              {fitOk ? `Las habitaciones alojan a tus ${travelers} viajeros.` : fieldErrors.rooms}
            </span>
          </div>

          <div className="resv-block">
            <span className="field-label">Datos de contacto</span>
            <div className="field-grid">
              <div className="field-block">
                <label htmlFor="resv-name">Nombre completo</label>
                <input id="resv-name" className="standalone-input" value={fullName} onChange={(event) => setFullName(event.target.value)} autoComplete="name" required aria-invalid={Boolean(fieldErrors.fullName)} />
                {fieldErrors.fullName && fullName && <span className="field-error" role="alert">{fieldErrors.fullName}</span>}
              </div>
              <div className="field-block">
                <label htmlFor="resv-phone">WhatsApp / teléfono</label>
                <input id="resv-phone" className="standalone-input" value={phone} onChange={(event) => setPhone(event.target.value)} inputMode="tel" autoComplete="tel" placeholder="+52 55 1234 5678" required aria-invalid={Boolean(fieldErrors.phone)} />
                {fieldErrors.phone && phone && <span className="field-error" role="alert">{fieldErrors.phone}</span>}
              </div>
            </div>
            <div className="field-block">
              <label htmlFor="resv-email">Correo electrónico</label>
              <input id="resv-email" className="standalone-input" value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" required aria-invalid={Boolean(fieldErrors.email)} />
              {fieldErrors.email && email && <span className="field-error" role="alert">{fieldErrors.email}</span>}
            </div>
          </div>

          <label className={`consent-row${passport ? " checked" : ""}`}>
            <input type="checkbox" checked={passport} onChange={(event) => setPassport(event.target.checked)} />
            <span className="consent-box" aria-hidden="true">{passport && <Check size={13} strokeWidth={3} />}</span>
            <span><strong>Confirmo que los pasajeros cuentan con pasaporte vigente.</strong><small>Requerido para continuar con la solicitud.</small></span>
          </label>
          <p className="resv-fine">Un agente de viajes de Aventuria te contactará para continuar con tu solicitud. Para avanzar con la reservación, necesitarás los pasaportes vigentes de todos los pasajeros.</p>

          {error && (
            <div className="form-error" role="alert">
              {error.join(" ")}
              {existing && (
                <div className="inline-actions" style={{ marginTop: 8 }}>
                  <a className="secondary-button small" href={`/solicitudes/${existing.id}`}>Ver solicitud {existing.folio}</a>
                  <button type="button" className="link-button" onClick={() => { setConfirmDuplicate(true); setExisting(null); setError(["Confirmado: se creará una nueva solicitud al enviar."]); }}>Crear otra de todos modos</button>
                </div>
              )}
            </div>
          )}
          <div className="resv-actions">
            <button type="submit" className="primary-button solid full" disabled={!valid || submitting}>
              <ButtonLabel busy={submitting} busyLabel="Enviando solicitud" label="Solicitar reservación" />
            </button>
            <span className="resv-helper">Sin costo ni compromiso · Respuesta en menos de 24 horas</span>
          </div>
        </form>
      </div>
    </Modal>
  );
}
